import { useEffect, useRef, useState, type ReactNode } from 'react';
import { rpcUrl } from '../lib/chains';
import { rpcApi, type MintedKey } from '../lib/rpc-client';
import { SaasError } from '../lib/saas-client';

/** Opens Stripe's setup Checkout for the card a postpaid plan's invoices are
 *  charged to; Stripe sends the customer back to `/account?rpc=card_saved`. */
export function useSaveCard() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await rpcApi.saveCard();
      if (url) window.location.assign(url);
    } catch (e) {
      setError(e instanceof SaasError ? e.message : 'Could not open Stripe.');
      setBusy(false);
    }
  }

  return { busy, error, save };
}

/** Copies `text`; says "Copied" for a moment. */
export function CopyButton({
  text,
  label = 'Copy',
  className = 'btn btn-sm',
  testid,
}: {
  text: string;
  label?: string;
  className?: string;
  testid?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  async function onCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard blocked (permissions, insecure context): the text stays
      // selectable on the page.
    }
  }
  return (
    <button type="button" className={className} onClick={onCopy} data-testid={testid}>
      {copied ? 'Copied' : label}
    </button>
  );
}

export function CodeBlock({ code, testid }: { code: string; testid?: string }) {
  return (
    <div className="rpc-code">
      <pre data-testid={testid}>
        <code>{code}</code>
      </pre>
      <CopyButton text={code} className="btn btn-sm rpc-code-copy" />
    </div>
  );
}

export function StatTile({
  label,
  value,
  sub,
  children,
  testid,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  children?: ReactNode;
  testid?: string;
}) {
  return (
    <div className="rpc-stat" data-testid={testid}>
      <div className="rpc-stat-label">{label}</div>
      <div className="rpc-stat-value">{value}</div>
      {children}
      {sub && <div className="rpc-stat-sub">{sub}</div>}
    </div>
  );
}

/** A filled bar for "used of included". Past 90% it turns gold. */
export function Meter({ used, total, label }: { used: number; total: number; label: string }) {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : used > 0 ? 100 : 0;
  return (
    <div
      className={`rpc-meter${pct >= 90 ? ' rpc-meter--high' : ''}`}
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={Math.min(used, total)}
    >
      <div className="rpc-meter-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** A key the customer must copy now: create and rotate are the only times
 *  saas returns it. */
export function KeyReveal({
  minted,
  onDismiss,
  title = 'Your new key',
  showUrl = true,
}: {
  minted: MintedKey;
  onDismiss: () => void;
  title?: string;
  /** Off where the page already shows the URL with this key. */
  showUrl?: boolean;
}) {
  const url = rpcUrl(1, minted.key);
  return (
    <section className="panel rpc-reveal" data-testid="key-reveal" aria-live="polite">
      <div className="panel-eyebrow">{title}</div>
      <p className="rpc-reveal-lead">
        <strong>{minted.name}</strong>: copy it now and keep it somewhere safe. We store
        only its hash, so this is the only time it's shown.
      </p>
      <div className="rpc-reveal-row">
        <code className="rpc-reveal-key" data-testid="key-reveal-value">
          {minted.key}
        </code>
        <CopyButton text={minted.key} label="Copy key" className="btn btn-primary btn-sm" />
      </div>
      {showUrl && (
        <>
          <p className="muted rpc-reveal-url-label">Your Ethereum URL with this key:</p>
          <div className="rpc-reveal-row">
            <code className="rpc-reveal-key">{url}</code>
            <CopyButton text={url} label="Copy URL" />
          </div>
        </>
      )}
      <button className="btn btn-sm" type="button" onClick={onDismiss} data-testid="key-reveal-dismiss">
        I've saved it
      </button>
    </section>
  );
}
